import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import Headline from "~/components/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { createReversalInvoice } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import InvoiceTotalsFormSection from "~/modules/billing/components/InvoiceTotalsFormSection";
import { DEFAULT_VAT_PERCENTAGE } from "~/modules/billing/constants/billingConstants.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	comment: string | null;
	invoiceSentAt: Date;
	recipientInformation: string | null;
	subtotalCents: number;
	travelCostCentsBeforeVat: number;
	vatPercentage: number;
}

type Props = {
	onCancel: () => void;
	onSuccess: (newInvoiceId: string) => void;
	originalInvoiceData: Invoice;
};

const today = new Date();

const CreateReversalInvoiceForm = ({
	onCancel,
	onSuccess,
	originalInvoiceData,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => {
		return yup.object({
			comment: yup.string().nullable().default(""),
			invoiceSentAt: yup.date().required(),
			recipientInformation: yup.string().nullable().default(""),
			subtotalCents: yup.number().required(),
			travelCostCentsBeforeVat: yup.number().required(),
			vatPercentage: yup.number().required(),
		});
	}, []);
	const defaultValues = useMemo(() => {
		return {
			comment: "",
			invoiceSentAt: new Date(),
			recipientInformation: "",
			subtotalCents: originalInvoiceData.subtotalCents * -1,
			travelCostCentsBeforeVat: originalInvoiceData.travelCostCentsBeforeVat * -1,
			vatPercentage: DEFAULT_VAT_PERCENTAGE,
		};
	}, [originalInvoiceData.subtotalCents, originalInvoiceData.travelCostCentsBeforeVat]);

	const {
		handleSubmit,
		control,
		formState: { isSubmitted, isValid },
		watch,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
		mode: "onChange",
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const updateData = {
				...data,
				invoiceSentAt: formatDateToYYYYMMDD(data.invoiceSentAt),
				originalInvoiceId: originalInvoiceData.id,
			};

			const res = await createReversalInvoice(updateData);
			const reversalInvoiceId = getDataFromResponse(res).reversalInvoiceId;
			onSuccess(reversalInvoiceId);
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [originalInvoiceData.id, onSuccess]);

	const formIsSubmittable = isValid && !isBusy;


	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Storno-Rechnung erzeugen</SidebarHeaderHeadline>
			</SidebarHeader>
			<div className="grow gap-x-8 overflow-auto px-7 py-2">
				{isBusy && <SidebarBusyOverlay />}
				{serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<div className="grid h-auto grid-cols-2 gap-4 pt-1">
					<div className="col-span-2">
						<Headline className="mb-2"
								  type="h4">Empfänger</Headline>
						<Input control={control}
							   label="Versendet an"
							   name="recipientInformation"
							   placeholder="Name, Versandart, ggf. E-Mail-Adresse"
						/>
					</div>
					<DatePickerInput name="invoiceSentAt"
									 label="Versendet am / Rechnungsdatum"
									 control={control}
									 minDate={today} />
					<InvoiceTotalsFormSection<FormData> control={control}
														watch={watch}
														title="Rechnungsbetrag" />
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Kommentar</Headline>
						<Textarea control={control}
								  name="comment"
								  placeholder="Hier können Besonderheiten zum Abrechnungsprozess vermerkt werden."
						/>
					</div>
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Rechnungsnummer</Headline>
						<p className="leading-tight text-gray-800">Die Rechnungsnummer wird beim Absenden dieses
							Formulars automatisch erzeugt und muss anschliessend im Rechnungsdokument nachgetragen
							werden.</p>
					</div>
				</div>
			</div>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Storno-Rechnung erzeugen
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default CreateReversalInvoiceForm;
